import { Button, Buttons, CancelButton, Field, Form, FormMessage, HelpTooltip, ModalCard, Radio } from '~/components';
import { useApi, useSession, useWorkspace } from '~/contexts';
import { Formik } from 'formik';
import { useIsMounted } from '~/hooks';
import React, { useState } from 'react';
import styled from 'styled-components';
import { emptyStringToNull } from '~/utils';
import * as Yup from 'yup';

const Description = styled.p`
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

const ErrorMessage = styled(FormMessage.Error)`
  margin-bottom: 1.5rem;
`;

const OptionInfo = styled.span`
  display: flex;
  flex-direction: column;
`;

const OptionDescription = styled.span`
  margin-top: 0.25rem;
  font-size: 0.875rem;
`;

export default function ManualSyncModal({ integration, onClose, onFinish }) {
  const api = useApi();
  const isMounted = useIsMounted();
  const { workspace } = useWorkspace();
  const { isAdmin } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState();

  const { hasCompletedFullSync, hasMigratedData, canRunIncremental } = integration.settings;
  const fullSyncLocked = (hasCompletedFullSync || hasMigratedData) && !isAdmin;
  const initialValues = { fullSync: !canRunIncremental };

  async function handleSubmit(values) {
    setIsLoading(true);
    setErrorMessage();

    try {
      await api.www.workspaces(workspace.id).harvest.manualSync(emptyStringToNull(values));
      onFinish();
      onClose();
    } catch ({ message }) {
      if (message) {
        setErrorMessage(message);
      } else {
        setErrorMessage('There was a problem completing your request. Please try again later.');
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }

  return (
    <ModalCard title="Harvest Migration" onClose={onClose}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({})}>
        {() => {
          return (
            <Form>
              <ModalCard.Body>
                {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
                <Description>
                  Run a one directional data import that populates members, clients, contacts, projects, invoices,
                  expenses, and time entries from Harvest.
                </Description>
                <Form.Control>
                  <Field.RadioGroup name="fullSync" direction="vertical">
                    <Radio
                      value={true}
                      disabled={fullSyncLocked}
                      label={
                        <OptionInfo>
                          Full Migration
                          <OptionDescription>
                            {fullSyncLocked
                              ? 'A full migration has already run. Contact Ruddr support if you need another one.'
                              : 'Pulls every record in Harvest and refreshes the data already migrated.'}
                          </OptionDescription>
                        </OptionInfo>
                      }
                    />
                    <Radio
                      value={false}
                      disabled={!canRunIncremental}
                      label={
                        <OptionInfo>
                          <span>
                            Incremental Updates
                            <HelpTooltip
                              message="Pulls only what changed in Harvest since the last migration. New records are added. Edited time entries, expenses, invoices, and payments are updated, whatever their date. Deletions in Harvest are not applied."
                              style={{ marginLeft: '.5rem' }}
                            />
                          </span>
                          <OptionDescription>
                            Pulls only the records added or changed in Harvest since the last completed migration. The
                            first migration is always full.
                          </OptionDescription>
                        </OptionInfo>
                      }
                    />
                  </Field.RadioGroup>
                </Form.Control>
              </ModalCard.Body>
              <ModalCard.Footer>
                <Buttons align="right">
                  <CancelButton onClick={onClose} style={{ marginRight: 'auto' }}>
                    Close
                  </CancelButton>
                  <Button type="submit" isLoading={isLoading}>
                    Start
                  </Button>
                </Buttons>
              </ModalCard.Footer>
            </Form>
          );
        }}
      </Formik>
    </ModalCard>
  );
}
